import React, { useEffect, useRef, useState } from 'react';
import { PhotoView } from 'react-photo-view';
import { renderAsync } from 'docx-preview';
import api from '../services/api';

// Seller verification documents can be images (jpg/png/webp, or extension-less
// older uploads), PDF, Word (.docx/.doc) or HEIC phone photos. Every type can be
// previewed in the browser; nothing is sent to a third-party viewer.
const extensionOf = (url) => {
  const match = /\.([a-z0-9]+)(?:[?#].*)?$/i.exec(url || '');
  return match ? match[1].toLowerCase() : '';
};

export const documentKind = (url) => {
  const ext = extensionOf(url);
  if (ext === 'pdf') return 'pdf';
  if (ext === 'docx') return 'docx';
  if (ext === 'doc') return 'doc';
  if (ext === 'heic' || ext === 'heif') return 'heic';
  return 'image';
};

export const isNonImageDoc = (url) => !!url && documentKind(url) !== 'image';

export const previewHint = (url) => {
  if (!url) return ' ';
  return documentKind(url) === 'image' ? 'Click to zoom' : 'Click Preview to view full screen';
};

const KIND_LABELS = {
  pdf: { icon: '📄', name: 'PDF document' },
  docx: { icon: '📝', name: 'Word document' },
  doc: { icon: '📝', name: 'Word document (.doc)' },
  heic: { icon: '🖼️', name: 'HEIC photo' }
};

const buttonStyle = {
  padding: '8px 16px',
  backgroundColor: '#2980b9',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 'bold'
};

const messageStyle = (color) => ({ textAlign: 'center', color, marginTop: '40px', padding: '0 20px' });

// ---- Full-screen viewer bodies, one per document kind ----

const DocxBody = ({ url, onStatus }) => {
  const containerRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => (cancelled || !containerRef.current
        ? undefined
        : renderAsync(blob, containerRef.current, undefined, { inWrapper: true, ignoreLastRenderedPageBreak: true })))
      .then(() => !cancelled && onStatus('ready'))
      .catch(() => !cancelled && onStatus('error'));
    return () => { cancelled = true; };
  }, [url, onStatus]);
  return <div ref={containerRef} />;
};

const DocBody = ({ url, onStatus }) => {
  const [html, setHtml] = useState(null);
  useEffect(() => {
    let cancelled = false;
    // Legacy .doc can't be parsed in the browser — the backend converts it to HTML.
    api.get('/admin/documents/doc-preview', {
      params: { path: new URL(url).pathname },
      responseType: 'text',
      timeout: 30000
    })
      .then((res) => { if (!cancelled) { setHtml(res.data); onStatus('ready'); } })
      .catch(() => !cancelled && onStatus('error'));
    return () => { cancelled = true; };
  }, [url, onStatus]);
  if (html === null) return null;
  return (
    <iframe
      title="Word document preview"
      // No scripts, no navigation: the HTML comes from a user-uploaded file.
      sandbox=""
      srcDoc={html}
      style={{ display: 'block', width: 'min(900px, 100%)', height: '100%', margin: '0 auto', border: 'none', backgroundColor: 'white' }}
    />
  );
};

const HeicBody = ({ url, label, onStatus }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    // Most browsers can't display HEIC; convert to JPEG locally (libheif WASM).
    Promise.all([fetch(url).then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.blob();
    }), import('heic-to')])
      .then(([blob, { heicTo }]) => heicTo({ blob, type: 'image/jpeg', quality: 0.92 }))
      .then((jpeg) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(jpeg);
        setSrc(objectUrl);
        onStatus('ready');
      })
      .catch(() => !cancelled && onStatus('error'));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, onStatus]);
  if (!src) return null;
  return <img src={src} alt={label} style={{ display: 'block', maxWidth: '100%', maxHeight: '100%', margin: '0 auto', objectFit: 'contain' }} />;
};

const DocumentViewer = ({ url, label, kind, onClose }) => {
  // PDFs render natively in the iframe, so they're ready immediately.
  const [status, setStatus] = useState(kind === 'pdf' ? 'ready' : 'loading');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.85)',
      zIndex: 1100,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '12px 20px',
        backgroundColor: '#2c3e50',
        color: 'white'
      }}>
        <strong>{label} — {KIND_LABELS[kind]?.name}</strong>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#aed6f1', fontSize: '14px' }}>
            Download original
          </a>
          <button onClick={onClose} style={{ ...buttonStyle, backgroundColor: '#e74c3c' }}>Close</button>
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'auto', backgroundColor: kind === 'heic' ? 'transparent' : '#ecf0f1' }}>
        {status === 'loading' && <p style={messageStyle(kind === 'heic' ? '#ecf0f1' : '#555')}>Loading preview…</p>}
        {status === 'error' && (
          <p style={messageStyle('#e74c3c')}>
            This document couldn't be previewed. Use "Download original" to open it instead.
          </p>
        )}
        {kind === 'pdf' && (
          <iframe title={label} src={url} style={{ display: 'block', width: '100%', height: '100%', border: 'none' }} />
        )}
        {kind === 'docx' && <DocxBody url={url} onStatus={setStatus} />}
        {kind === 'doc' && <DocBody url={url} onStatus={setStatus} />}
        {kind === 'heic' && <HeicBody url={url} label={label} onStatus={setStatus} />}
      </div>
    </div>
  );
};

// ---- Card shown inside the admin modals ----

const DocumentPreview = ({ url, label, height = '250px', emptyText = 'Not uploaded' }) => {
  const [open, setOpen] = useState(false);

  if (!url) {
    return (
      <div style={{
        width: '100%',
        height,
        borderRadius: '8px',
        backgroundColor: '#f8f9fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#999',
        border: '2px dashed #ddd'
      }}>
        {emptyText}
      </div>
    );
  }

  const kind = documentKind(url);

  if (kind === 'image') {
    return (
      <PhotoView src={url}>
        <img
          src={url}
          alt={label}
          style={{
            width: '100%',
            height,
            objectFit: 'cover',
            borderRadius: '8px',
            cursor: 'pointer',
            border: '2px solid #ddd'
          }}
        />
      </PhotoView>
    );
  }

  return (
    <>
      <div style={{
        position: 'relative',
        width: '100%',
        height,
        borderRadius: '8px',
        backgroundColor: '#f0f4ff',
        border: '2px solid #c5d3f0',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        {kind === 'pdf' ? (
          // First page thumbnail; pointer-events off so the whole card opens the viewer.
          <iframe
            title={`${label} thumbnail`}
            src={`${url}#toolbar=0&navpanes=0&view=FitH`}
            tabIndex={-1}
            style={{ width: '100%', height: '100%', border: 'none', pointerEvents: 'none' }}
          />
        ) : (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#34495e' }}>
            <div style={{ fontSize: '40px', marginBottom: '8px' }}>{KIND_LABELS[kind].icon}</div>
            <div style={{ fontSize: '13px' }}>{KIND_LABELS[kind].name}</div>
          </div>
        )}
        <button
          onClick={() => setOpen(true)}
          aria-label={`Preview ${label}`}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            paddingBottom: '12px'
          }}
        >
          <span style={buttonStyle}>Preview</span>
        </button>
      </div>
      {open && <DocumentViewer url={url} label={label} kind={kind} onClose={() => setOpen(false)} />}
    </>
  );
};

export default DocumentPreview;
